import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { ArrowRight } from 'lucide-react';

const impactASPlusLogo = "/opt/brand/impactas-final-2.webp";

// Outcome cards data
const outcomeCards = [
  {
    title: "Pipeline Clarity",
    description: "A clear view of leadership depth, bench strength, and critical role exposure."
  },
  {
    title: "Readiness & Risk Insight",
    description: "Evidence-led understanding of capability, derailers, and decision quality under pressure."
  },
  {
    title: "Targeted Development",
    description: "Focused development priorities linked to role demands and organisational strategy."
  },
  {
    title: "Stronger Transitions",
    description: "Practical transition planning that reduces disruption and protects performance."
  }
];

// Process steps data
const processSteps = [
  {
    step: 1,
    title: "Clarify the succession context",
    description: "Define critical roles, leadership standards, and the strategic horizon."
  },
  {
    step: 2,
    title: "Assess readiness and behavioural risk",
    description: "Combine validated diagnostics, structured insight, and context-based evaluation."
  },
  {
    step: 3,
    title: "Map the leadership pipeline",
    description: "Build a role-by-role view of bench strength, gaps, and dependencies."
  },
  {
    step: 4,
    title: "Design targeted development",
    description: "Create action-focused development pathways for priority cohorts and individuals."
  },
  {
    step: 5,
    title: "Embed governance and review",
    description: "Establish review cadence, decision standards, and measurement to sustain momentum."
  }
];

// Use cases data
const useCases = [
  "CEO and ExCo succession planning",
  "Critical role readiness and promotion decisions",
  "Leadership pipeline reviews for fast-growing organisations",
  "Regulated environment leadership assurance",
  "Post-merger or restructuring leadership continuity",
  "High-potential identification and acceleration"
];

// Audience blocks data
const audienceBlocks = [
  {
    title: "Boards and Board Committees",
    description: "Independent, evidence-based insight for high-stakes appointments and continuity."
  },
  {
    title: "CEOs and Executive Committees",
    description: "A clear view of readiness, risk, and development priorities linked to strategy."
  },
  {
    title: "CHROs and Talent Leaders",
    description: "A structured system for pipeline visibility, decisions, and sustained development."
  }
];

const LeadershipSuccessionPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Succession Planning Nigeria | Leadership Pipeline Assessment Lagos | RESILIGENT"
        description="Data-led succession planning and leadership pipeline intelligence for Nigerian organisations. Evidence-based CEO succession, executive readiness assessment, and leadership continuity planning. Trusted by top banks and corporates across Africa."
        keywords="succession planning Nigeria, leadership pipeline Lagos, CEO succession Africa, executive succession planning Nigeria, leadership readiness assessment Lagos, board succession Nigeria, leadership continuity planning Africa, succession intelligence Nigeria"
        canonical="/leadership-succession-intelligence"
      />
      <Header />
      
      <main>
        {/* SECTION 1: HERO */}
        <section 
          className="relative min-h-[70vh] lg:min-h-[75vh] flex items-center bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(10, 10, 10, 0.88) 0%, rgba(10, 10, 10, 0.7) 40%, rgba(10, 10, 10, 0.5) 100%), url('/opt/photo/ext/u1573164574572-cb89e39749b4.webp')`
          }}
        >
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8 pt-28 pb-16">
            <div className="max-w-3xl">
              <h1 className="text-white font-semibold text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.1] mb-8">
                Leadership & Succession Intelligence
              </h1>
              
              <p className="text-slate-300 text-lg lg:text-xl leading-relaxed mb-6">
                Board-ready insight into leadership pipelines, readiness, and transition risk, enabling confident appointments and sustained performance.
              </p>
              
              <p className="text-slate-500 text-sm mb-10">
                Designed for regulated, high-growth, and high-stakes environments.
              </p>
              
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                <a
                  href="/contact"
                  className="inline-flex items-center gap-2 bg-[#2693d0] text-white px-6 py-3.5 rounded font-medium text-base hover:bg-[#1e7ab8] transition-all duration-300"
                >
                  Speak to an Advisor
                  <ArrowRight size={18} />
                </a>
                <a
                  href="#how-it-works"
                  className="text-slate-400 hover:text-white font-medium text-base transition-colors duration-300"
                >
                  See how it works
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2: THE PROBLEM (WHY THIS MATTERS) */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                Succession is not a plan. It is an organisational risk.
              </h2>
              
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-8">
                Many organisations rely on informal assumptions about who is ready for bigger roles. When leadership transitions happen under pressure, capability gaps and behavioural risk surface quickly, damaging execution, trust, and culture.
              </p>
              
              <p className="text-slate-800 font-semibold text-lg lg:text-xl leading-relaxed">
                Succession intelligence replaces opinion with evidence.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 3: WHAT THIS DOMAIN DELIVERS (OUTCOMES) */}
        <section className="bg-[#f8fafc] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-12 leading-tight">
              What you gain
            </h2>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {outcomeCards.map((card, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl p-6 lg:p-8 card-pulse-border hover:border-[#2693d0]/30 hover:shadow-lg transition-all duration-300"
                >
                  <h3 className="text-slate-800 font-semibold text-lg lg:text-xl mb-4">
                    {card.title}
                  </h3>
                  <p className="text-slate-600 text-sm lg:text-base leading-relaxed">
                    {card.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 4: HOW IT WORKS (PROCESS) */}
        <section id="how-it-works" className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-12 leading-tight">
              How it works
            </h2>
            
            <div className="space-y-0">
              {processSteps.map((step, index) => (
                <div 
                  key={index}
                  className="relative flex gap-6 lg:gap-10 pb-10 last:pb-0"
                >
                  {/* Step number and line */}
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-[#2693d0]/10 flex items-center justify-center flex-shrink-0 border-2 border-[#2693d0]/30">
                      <span className="text-[#2693d0] font-bold text-lg">{step.step}</span>
                    </div>
                    {index < processSteps.length - 1 && (
                      <div className="w-px h-full bg-slate-200 mt-3" />
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 pb-6">
                    <h3 className="text-slate-800 font-semibold text-lg lg:text-xl mb-2">
                      {step.title}
                    </h3>
                    <p className="text-slate-600 text-base leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 5: IMPACT AS PLUS™ INTEGRATION */}
        <section className="bg-[#1e3a5f] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <img 
                src={impactASPlusLogo} 
                alt="Impact AS Plus" 
                className="h-10 lg:h-12 w-auto mb-8 opacity-90"
              />
              
              <h2 className="text-white font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                Powered by Impact AS Plus™
              </h2>
              
              <p className="text-slate-200 text-base lg:text-lg leading-relaxed mb-6">
                Leadership & Succession Intelligence is supported by Impact AS Plus™, RESILIGENT&apos;s leadership intelligence architecture that integrates diagnostics, insight, coaching, and organisational data.
              </p>
              
              <p className="text-slate-300 text-base lg:text-lg leading-relaxed mb-8">
                This enables a coherent view of leadership capability, behavioural risk, and progress over time, supporting better decisions and measurable development.
              </p>
              
              <a
                href="/impact-as-plus"
                className="inline-flex items-center gap-2 text-[#2693d0] hover:text-white font-medium text-base transition-colors duration-300"
              >
                Explore Impact AS Plus™
                <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </section>

        {/* SECTION 6: WHERE THIS IS USED (USE CASES) */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-10 leading-tight">
                Common use cases
              </h2>
              
              <ul className="space-y-4">
                {useCases.map((useCase, index) => (
                  <li key={index} className="flex items-start gap-4">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{useCase}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* SECTION 7: WHO THIS IS FOR (AUDIENCE) */}
        <section className="bg-[#f8fafc] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-12 leading-tight">
              Who it is for
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
              {audienceBlocks.map((block, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl p-6 lg:p-8 card-pulse-border"
                >
                  <h3 className="text-slate-800 font-semibold text-lg lg:text-xl mb-4">
                    {block.title}
                  </h3>
                  <p className="text-slate-600 text-sm lg:text-base leading-relaxed">
                    {block.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 8: FINAL CTA */}
        <section className="bg-[#d9e8f0] py-20 lg:py-28 border-t border-slate-300">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                Strengthen leadership continuity with evidence, not assumptions.
              </h2>
              
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-10">
                Book a confidential conversation to explore your pipeline priorities and transition risks.
              </p>
              
              <a
                href="/contact"
                className="inline-flex items-center gap-2 bg-[#2693d0] text-white px-8 py-4 rounded font-medium text-base hover:bg-[#1e7ab8] transition-all duration-300"
              >
                Book a confidential conversation
                <ArrowRight size={18} />
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer hidePreFooterCta={true} />
    </div>
  );
};

export default LeadershipSuccessionPage;
